import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Plus, Trash2, X, Printer, Send, ArrowRightLeft, Loader2, Eye, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { api, currency } from "./api";
import { ASSETS } from "@/data/content";

const DOC_STATUS = ["Draft", "Sent", "Accepted", "Paid", "Overdue", "Cancelled"];
const PAYMENT_METHODS = ["EFT / Bank Transfer", "Card", "Cash", "PayFast", "Other"];
const today = () => new Date().toISOString().slice(0, 10);

const emptyDoc = (over = {}) => ({
  doc_type: "Quote",
  client_name: "",
  client_email: "",
  client_phone: "",
  client_company: "",
  vat_number: "",
  issue_date: today(),
  due_date: "",
  line_items: [{ description: "", qty: 1, rate: 0 }],
  tax_vat_percent: 0,
  currency: "ZAR",
  notes: "",
  ...over,
});

const field =
  "w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-white/40";
const lbl = "mb-1 block text-[11px] uppercase tracking-widest text-neutral-500";

export default function Invoices() {
  const [docs, setDocs] = useState([]);
  const [config, setConfig] = useState(null);
  const [builder, setBuilder] = useState(null); // form object or null
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const [sp, setSp] = useSearchParams();

  const load = async () => {
    try {
      const [d, c] = await Promise.all([api.get("/invoices"), api.get("/config")]);
      setDocs(d.data);
      setConfig(c.data);
    } catch {
      toast.error("Failed to load documents");
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Prefill from inquiry or open blank via ?new=1
  useEffect(() => {
    const inq = location.state?.fromInquiry;
    if (inq) {
      setEditingId(null);
      setBuilder(
        emptyDoc({
          client_name: inq.full_name,
          client_email: inq.email,
          client_phone: inq.phone || "",
          linked_inquiry_id: inq.id,
          notes: `Brief: ${inq.details}`,
          line_items: [{ description: inq.service, qty: 1, rate: 0 }],
        })
      );
      window.history.replaceState({}, "");
    } else if (sp.get("new")) {
      setEditingId(null);
      setBuilder(emptyDoc());
      setSp({}, { replace: true });
    }
  }, [location.state, sp, setSp]);

  const totals = useMemo(() => {
    if (!builder) return { sub: 0, total: 0 };
    const sub = builder.line_items.reduce(
      (s, li) => s + Number(li.qty || 0) * Number(li.rate || 0),
      0
    );
    return { sub, total: sub * (1 + Number(builder.tax_vat_percent || 0) / 100) };
  }, [builder]);

  const setLi = (i, key, val) =>
    setBuilder((b) => {
      const items = [...b.line_items];
      items[i] = { ...items[i], [key]: val };
      return { ...b, line_items: items };
    });
  const addLi = () =>
    setBuilder((b) => ({ ...b, line_items: [...b.line_items, { description: "", qty: 1, rate: 0 }] }));
  const removeLi = (i) =>
    setBuilder((b) => ({ ...b, line_items: b.line_items.filter((_, x) => x !== i) }));

  const save = async () => {
    if (!builder.client_name || !builder.client_email)
      return toast.error("Client name and email are required");
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/invoices/${editingId}`, builder);
        toast.success("Document updated");
      } else {
        await api.post("/invoices", builder);
        toast.success("Document created");
      }
      setBuilder(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const edit = (d) => {
    setEditingId(d.id);
    setBuilder({ ...emptyDoc(), ...d });
  };
  const convert = async (d) => {
    try {
      await api.post(`/invoices/${d.id}/convert`);
      toast.success("Converted to Tax Invoice");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    }
  };
  const send = async (d) => {
    try {
      await api.post(`/invoices/${d.id}/send`);
      toast.success(`Sent to ${d.client_email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const markPaid = async (id, payment_method, paid_date) => {
    try {
      await api.post(`/invoices/${id}/mark-paid`, { payment_method, paid_date });
      toast.success("Marked as paid");
      setPayFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold">Quotes & Invoices</h1>
        <button
          data-testid="create-doc-btn"
          onClick={() => {
            setEditingId(null);
            setBuilder(emptyDoc());
          }}
          className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-medium text-black hover:bg-neutral-200"
        >
          <Plus size={15} /> New document
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[640px] text-left text-sm" data-testid="documents-table">
          <thead className="bg-white/[0.03] text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3">Number</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-neutral-500">
                  No documents yet.
                </td>
              </tr>
            ) : (
              docs.map((d) => (
                <tr key={d.id} className="border-t border-white/5" data-testid={`doc-row-${d.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{d.doc_number}</td>
                  <td className="px-4 py-3">{d.doc_type}</td>
                  <td className="px-4 py-3">
                    <div>{d.client_name}</div>
                    <div className="text-xs text-neutral-500">{d.client_email}</div>
                  </td>
                  <td className="px-4 py-3">{currency(d.total_amount, d.currency)}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs">
                      {d.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button title="Preview" onClick={() => setPreview(d)} data-testid={`preview-${d.id}`} className="rounded-lg p-2 hover:bg-white/10">
                        <Eye size={15} />
                      </button>
                      <button title="Edit" onClick={() => edit(d)} className="rounded-lg px-2 py-2 text-xs hover:bg-white/10">
                        Edit
                      </button>
                      <button title="Send" onClick={() => send(d)} data-testid={`send-${d.id}`} className="rounded-lg p-2 hover:bg-white/10">
                        <Send size={15} />
                      </button>
                      {d.doc_type === "Quote" && (
                        <button title="Convert to Invoice" onClick={() => convert(d)} data-testid={`convert-${d.id}`} className="rounded-lg p-2 hover:bg-white/10">
                          <ArrowRightLeft size={15} />
                        </button>
                      )}
                      {d.doc_type === "Invoice" && d.status !== "Paid" && (
                        <button title="Mark Paid" onClick={() => setPayFor(d)} data-testid={`mark-paid-${d.id}`} className="rounded-lg p-2 text-emerald-300 hover:bg-white/10">
                          <CheckCircle size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {builder && (
        <Builder
          builder={builder}
          setBuilder={setBuilder}
          config={config}
          totals={totals}
          setLi={setLi}
          addLi={addLi}
          removeLi={removeLi}
          onClose={() => {
            setBuilder(null);
            setEditingId(null);
          }}
          onSave={save}
          busy={busy}
          editing={!!editingId}
        />
      )}

      {preview && <Preview doc={preview} onClose={() => setPreview(null)} />}
      {payFor && <PayModal doc={payFor} onClose={() => setPayFor(null)} onConfirm={markPaid} />}
    </div>
  );
}

function PayModal({ doc, onClose, onConfirm }) {
  const [method, setMethod] = useState(PAYMENT_METHODS[0]);
  const [date, setDate] = useState(today());
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="pay-modal">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0e0e11] p-6 text-white">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Record payment</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <p className="mb-4 text-sm text-neutral-400">
          {doc.doc_number} · {currency(doc.total_amount, doc.currency)}
        </p>
        <label className={lbl}>Payment method</label>
        <select data-testid="pay-method" className={field} value={method} onChange={(e) => setMethod(e.target.value)}>
          {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <label className={`${lbl} mt-3`}>Payment date</label>
        <input data-testid="pay-date" type="date" className={field} value={date} onChange={(e) => setDate(e.target.value)} />
        <button
          data-testid="confirm-paid"
          onClick={() => onConfirm(doc.id, method, date)}
          className="mt-5 w-full rounded-xl bg-emerald-500 py-3 font-medium text-black hover:bg-emerald-400"
        >
          Mark as Paid
        </button>
      </div>
    </div>
  );
}

function Builder({ builder, setBuilder, config, totals, setLi, addLi, removeLi, onClose, onSave, busy, editing }) {
  const set = (k) => (e) => setBuilder((b) => ({ ...b, [k]: e.target.value }));
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="doc-builder">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative h-full w-full max-w-2xl overflow-y-auto border-l border-white/10 bg-[#0e0e11] p-6 text-white">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold">
            {editing ? "Edit document" : "New document"}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="mb-4 flex gap-2">
          {["Quote", "Invoice"].map((t) => (
            <button
              key={t}
              data-testid={`type-${t}`}
              onClick={() => setBuilder((b) => ({ ...b, doc_type: t }))}
              className={`flex-1 rounded-xl border py-2.5 text-sm ${
                builder.doc_type === t ? "border-white bg-white text-black" : "border-white/10 text-neutral-300"
              }`}
            >
              {t === "Quote" ? "Quote / Estimate" : "Tax Invoice"}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className={lbl}>Client name *</label><input data-testid="client-name" className={field} value={builder.client_name} onChange={set("client_name")} /></div>
          <div><label className={lbl}>Client email *</label><input data-testid="client-email" className={field} value={builder.client_email} onChange={set("client_email")} /></div>
          <div><label className={lbl}>Client phone</label><input className={field} value={builder.client_phone} onChange={set("client_phone")} /></div>
          <div><label className={lbl}>Company</label><input className={field} value={builder.client_company} onChange={set("client_company")} /></div>
          <div><label className={lbl}>Client VAT no.</label><input className={field} value={builder.vat_number} onChange={set("vat_number")} /></div>
          <div><label className={lbl}>Issue date</label><input type="date" className={field} value={builder.issue_date} onChange={set("issue_date")} /></div>
          <div><label className={lbl}>{builder.doc_type === "Quote" ? "Valid until" : "Due date"}</label><input type="date" className={field} value={builder.due_date} onChange={set("due_date")} /></div>
        </div>

        <div className="mt-5 flex items-center justify-between">
          <p className="text-xs uppercase tracking-widest text-neutral-500">Line items</p>
          {config?.service_presets && (
            <select
              data-testid="preset-select"
              className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-xs"
              value=""
              onChange={(e) => {
                const p = config.service_presets.find((x) => x.description === e.target.value);
                if (p) setBuilder((b) => ({ ...b, line_items: [...b.line_items, { description: p.description, qty: 1, rate: p.rate }] }));
              }}
            >
              <option value="">+ Add preset</option>
              {config.service_presets.map((p) => (
                <option key={p.description} value={p.description}>
                  {p.description} — {currency(p.rate, builder.currency)}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="mt-2 space-y-2">
          {builder.line_items.map((li, i) => (
            <div key={i} className="flex gap-2" data-testid={`line-item-${i}`}>
              <input className={`${field} flex-1`} placeholder="Description" value={li.description} onChange={(e) => setLi(i, "description", e.target.value)} />
              <input className={`${field} w-16`} type="number" value={li.qty} onChange={(e) => setLi(i, "qty", e.target.value)} />
              <input className={`${field} w-28`} type="number" value={li.rate} onChange={(e) => setLi(i, "rate", e.target.value)} />
              <button onClick={() => removeLi(i)} className="rounded-lg px-2 text-neutral-400 hover:bg-white/10">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <button onClick={addLi} data-testid="add-line-item" className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs hover:bg-white/10">
            <Plus size={14} /> Add line
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div><label className={lbl}>Currency</label>
            <select className={field} value={builder.currency} onChange={set("currency")}>
              <option value="ZAR">ZAR (R)</option>
              <option value="USD">USD ($)</option>
            </select>
          </div>
          <div><label className={lbl}>VAT %</label><input type="number" className={field} value={builder.tax_vat_percent} onChange={set("tax_vat_percent")} /></div>
        </div>
        <div className="mt-3"><label className={lbl}>Notes</label><textarea className={`${field} min-h-[70px]`} value={builder.notes} onChange={set("notes")} /></div>

        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
          <div className="flex justify-between text-neutral-400"><span>Subtotal</span><span>{currency(totals.sub, builder.currency)}</span></div>
          <div className="flex justify-between text-neutral-400"><span>VAT ({builder.tax_vat_percent || 0}%)</span><span>{currency(totals.total - totals.sub, builder.currency)}</span></div>
          <div className="mt-1 flex justify-between text-base font-semibold"><span>Total</span><span data-testid="builder-total">{currency(totals.total, builder.currency)}</span></div>
        </div>

        <button onClick={onSave} disabled={busy} data-testid="save-doc" className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-white py-3.5 font-medium text-black hover:bg-neutral-200 disabled:opacity-60">
          {busy ? <Loader2 size={18} className="animate-spin" /> : editing ? "Update document" : "Create document"}
        </button>
      </div>
    </div>
  );
}

function Preview({ doc, onClose }) {
  const cur = doc.currency || "ZAR";
  const co = doc.company || {};
  const bank = doc.banking_details || {};
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70 p-4 sm:p-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-3 flex justify-end gap-2">
          <button onClick={() => window.print()} data-testid="print-doc" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-black">
            <Printer size={15} /> Print / Download PDF
          </button>
          <button onClick={onClose} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white">
            <X size={15} /> Close
          </button>
        </div>
        <div id="a4-print" className="rounded-lg bg-white p-8 text-[#0a0a0a] shadow-2xl sm:p-12">
          <div className="flex items-start justify-between border-b border-neutral-200 pb-6">
            <div>
              <img src={ASSETS.logo} alt="A Flat Records" className="mb-3 h-8 w-auto" />
              <h2 className="font-heading text-2xl font-bold tracking-tight">A FLAT RECORDS</h2>
              <p className="mt-1 text-xs text-neutral-500">{co.address}</p>
              <p className="text-xs text-neutral-500">Reg: {co.reg}</p>
              <p className="text-xs text-neutral-500">{co.email}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold">{doc.doc_type === "Quote" ? "QUOTE" : "TAX INVOICE"}</p>
              <p className="font-mono text-sm text-neutral-600">{doc.doc_number}</p>
              <p className="mt-2 text-xs text-neutral-500">Issue: {doc.issue_date}</p>
              {doc.due_date ? (
                <p className="text-xs text-neutral-500">
                  {doc.doc_type === "Quote" ? "Valid until" : "Due"}: {doc.due_date}
                </p>
              ) : null}
              {doc.status === "Paid" ? (
                <span className="mt-3 inline-block rounded-md border-2 border-emerald-600 px-3 py-1 text-sm font-bold uppercase tracking-wider text-emerald-600">
                  Paid
                </span>
              ) : null}
            </div>
          </div>

          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-neutral-400">Bill to</p>
            <p className="font-semibold">{doc.client_name}</p>
            {doc.client_company ? <p className="text-sm">{doc.client_company}</p> : null}
            <p className="text-sm text-neutral-600">{doc.client_email}</p>
            {doc.client_phone ? <p className="text-sm text-neutral-600">{doc.client_phone}</p> : null}
            {doc.vat_number ? <p className="text-sm text-neutral-600">VAT: {doc.vat_number}</p> : null}
          </div>

          <table className="mt-6 w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-300 text-left text-xs uppercase text-neutral-500">
                <th className="py-2">Description</th>
                <th className="py-2 text-right">Qty</th>
                <th className="py-2 text-right">Rate</th>
                <th className="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(doc.line_items || []).map((li, i) => (
                <tr key={i} className="border-b border-neutral-100">
                  <td className="py-2">{li.description}</td>
                  <td className="py-2 text-right">{li.qty}</td>
                  <td className="py-2 text-right">{currency(li.rate, cur)}</td>
                  <td className="py-2 text-right">{currency(li.amount, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 flex justify-end">
            <div className="w-56 text-sm">
              <div className="flex justify-between py-1"><span className="text-neutral-500">Subtotal</span><span>{currency(doc.subtotal, cur)}</span></div>
              <div className="flex justify-between py-1"><span className="text-neutral-500">VAT ({doc.tax_vat_percent || 0}%)</span><span>{currency((doc.total_amount || 0) - (doc.subtotal || 0), cur)}</span></div>
              <div className="mt-1 flex justify-between border-t border-neutral-300 py-2 text-base font-bold"><span>Total</span><span>{currency(doc.total_amount, cur)}</span></div>
            </div>
          </div>

          {doc.notes ? <p className="mt-6 whitespace-pre-wrap text-xs text-neutral-600">{doc.notes}</p> : null}

          {doc.status === "Paid" && doc.paid_date ? (
            <p className="mt-4 text-xs text-emerald-700">
              Payment received on {doc.paid_date} via {doc.payment_method}.
            </p>
          ) : null}

          <div className="mt-8 border-t border-neutral-200 pt-4 text-xs text-neutral-600">
            <p className="font-semibold text-neutral-800">Banking details</p>
            <p>{bank.bank} · {bank.account_name}</p>
            <p>Acc {bank.account_number} · Branch {bank.branch_code} · SWIFT {bank.swift}</p>
          </div>
          <p className="mt-6 text-center text-[11px] text-neutral-400">
            A Flat Records · Precision in sound. Independence in motion.
          </p>
        </div>
      </div>
    </div>
  );
}
